import { match, compile, MatchFunction, MatchResult } from "path-to-regexp";

import createQueryStringFromObject, {
  UrlQueryObject,
} from "./createQueryStringFromObject";
import { nationalCurriculumInsightsGuidanceHref } from "./nationalCurriculumInsights";

import { PageNameValueType } from "@/browser-lib/avo/Avo";
import isBrowser from "@/utils/isBrowser";
import errorReporter from "@/common-lib/error-reporter";
import OakError from "@/errors/OakError";
import getBrowserConfig from "@/browser-lib/getBrowserConfig";
import { SearchQuery } from "@/context/Search/search.types";
import {
  PreselectedDownloadType,
  PreselectedShareType,
} from "@/components/TeacherComponents/types/downloadAndShare.types";

const reportError = errorReporter("urls.ts");

/**
 * type pattern below is to allow any string value whilst offering autocomplete
 * on a union type specified. E.g. type A =  OrString<"foo" | "bar"> would
 * allow strings, but would autocomplete with "foo", "bar"
 */
type OrString<T extends string> = T | (string & Record<never, never>);

export type OakPageType = keyof OakPages;
export type OakHref = ReturnType<OakPages[OakPageType]["resolveHref"]>;

export type MaybeOakHref = OrString<OakHref>;
export type MaybeOakPageType = OrString<OakPageType>;

export type AnalyticsPageName = PageNameValueType | ExternalPageName;

const getCurrentHostname = () => {
  if (isBrowser) {
    return window.location.hostname;
  }
  return getBrowserConfig("clientAppBaseUrl");
};
export const isOakPage = (page: MaybeOakPageType): page is OakPageType => {
  return Object.keys(OAK_PAGES).includes(page);
};
export const isExternalHref = (href: MaybeOakHref) => {
  if (href.startsWith("#")) {
    return false;
  }
  if (href.startsWith("/")) {
    return false;
  }

  try {
    const url = new URL(href);

    if (url.hostname === getCurrentHostname()) {
      return false;
    }
  } catch (error) {
    reportError(error, { href });
  }

  return true;
};

export type BlogListingLinkProps = {
  page: "blog-index";
  categorySlug?: string | null;
  search?: {
    page?: string;
  };
};
export type WebinarListingLinkProps = {
  page: "webinar-index";
  categorySlug?: string | null;
  search?: {
    page?: string;
  };
};

export type UnitOverviewLinkProps = {
  page: "unit-overview";
  programmeSlug: string;
  unitSlug: string;
  query?: {
    subject_category?: string;
  };
};

export type LessonOverviewLinkProps = {
  page: "lesson-overview";
  programmeSlug: string;
  unitSlug: string;
  lessonSlug: string;
};

export type TeachersHomePageProps = {
  page: "teachers-home-page";
};

export type PupilLessonLinkProps = {
  page: "pupil-lesson";
  programmeSlug: string;
  unitSlug: string;
  lessonSlug: string;
};

export type PupilLessonResultsLinkProps = {
  page: "pupil-lesson-results";
  programmeSlug: string;
  unitSlug: string;
  lessonSlug: string;
  attemptId: string;
};

export type PupilLessonResultsCanonicalShareLinkProps = {
  page: "pupil-lesson-results-canonical-share";
  lessonSlug: string;
  attemptId: string;
};
export type PupilLessonResultsCanonicalPrintableLinkProps = {
  page: "pupil-lesson-results-canonical-printable";
  lessonSlug: string;
  attemptId: string;
};
export type PupilLessonListingLinkProps = {
  page: "pupil-lesson-index";
  programmeSlug: string;
  unitSlug: string;
};

export type PupilProgrammeListingLinkProps = {
  page: "pupil-programme-index";
  programmeSlug: string;
  optionSlug: string;
};

export type PupilUnitListingLinkProps = {
  page: "pupil-unit-index";
  programmeSlug: string;
};

export type PupilSubjectListingLinkProps = {
  page: "pupil-subject-index";
  yearSlug: string;
};

export type PupilYearListingLinkProps = {
  page: "pupil-year-index";
};

type LessonOverviewCanonicalLinkProps = {
  page: "lesson-overview-canonical";
  lessonSlug: string;
};

export type LessonDownloadsLinkProps = {
  page: "lesson-downloads";
  programmeSlug: string;
  unitSlug: string;
  lessonSlug: string;
  query?: {
    preselected: PreselectedDownloadType | null;
  };
};

export type LessonDownloadsSuccessLinkProps = {
  page: "lesson-downloads-success";
  programmeSlug: string;
  unitSlug: string;
  lessonSlug: string;
};

export type LessonDownloadsCanonicalLinkProps = {
  page: "lesson-downloads-canonical";
  lessonSlug: string;
  downloads: "downloads";
  query?: {
    preselected: PreselectedDownloadType | null;
  };
};

export type LessonMediaLinkProps = {
  page: "lesson-media";
  programmeSlug: string;
  unitSlug: string;
  lessonSlug: string;
  query?: {
    video: string;
  };
};

export type LessonMediaCanonicalLinkProps = {
  page: "lesson-media-canonical";
  lessonSlug: string;
  query?: {
    video: string;
  };
};

export type LessonShareLinkProps = {
  page: "lesson-share";
  programmeSlug: string;
  unitSlug: string;
  lessonSlug: string;
  query?: {
    preselected: PreselectedShareType | null;
  };
};

type SearchLinkProps = {
  page: "search";
  query?: Partial<SearchQuery>;
};
type LandingPageLinkProps = { page: "landing-page"; lpSlug: string };

type WebinarSingleLinkProps = { page: "webinar-single"; webinarSlug: string };
type BlogSingleLinkProps = { page: "blog-single"; blogSlug: string };
type CampaignSingleLinkProps = {
  page: "campaign-single";
  campaignSlug: string;
};
type AboutUsWhoWeAreLinkProps = { page: "about-who-we-are" };

type AboutUsMeetTheTeamLinkProps = { page: "about-meet-the-team" };
type AboutUsMeetTheTeamBioLinkProps = {
  page: "about-meet-the-team-bio";
  slug: string;
};
type AboutUsGetInvolvedLinkProps = { page: "about-get-involved" };
type AboutUsJoinResearchPanelLinkProps = { page: "about-join-research-panel" };
type AboutUsOaksCurriculaLinkProps = { page: "about-oaks-curricula" };
type AboutUsOaksImpactLinkProps = { page: "about-oaks-impact" };
type AboutUsCaseStudyLinkProps = { page: "about-case-study"; slug: string };
type AboutUsCaseStudyLibraryLinkProps = { page: "about-case-study-library" };

type CareersLinkProps = { page: "careers" };
type ContactUsLinkProps = { page: "contact" };
type FontHelpLinkProps = { page: "help-font" };
type HelpLinkProps = { page: "help" };
type PupilHelpLinkProps = { page: "pupil-help" };
type GuideToOakLinkProps = { page: "guide-to-oak" };
type HomeLinkProps = { page: "home" };
type LessonPlanningLinkProps = { page: "lesson-planning" };
type CurriculumChangeExplainedLinkProps = {
  page: "curriculum-change-explained";
};
type LegalLinkProps = {
  page: "legal";
  /**
   * The slug for legal pages comes from Sanity so should technically be a
   * string, but the assumption is that the slugs will not be changing from
   * their current values:
   */
  legalSlug: OrString<"privacy-policy" | "terms-and-conditions">;
};
type OurTeachersLinkProps = { page: "our-teachers" };
type OakCurriculumLinkProps = { page: "oak-curriculum" };
type ClassroomLinkProps = { page: "classroom" };
type LabsLinkProps = { page: "labs" };
type LabsTeachingMaterialsLinkProps = {
  page: "labs-teaching-materials";
  query?: UrlQueryObject;
};
type McpLinkProps = { page: "mcp" };
type TeacherHubLinkProps = { page: "teacher-hub" };

type OnboardingLinkProps = {
  page: "onboarding";
  query?: {
    returnTo: string;
  };
};

type OnboardingSchoolSelectionLinkProps = {
  page: "onboarding-school-selection";
};

type OnboardingRoleSelectionLinkProps = {
  page: "onboarding-role-selection";
};

type OnboardingUseOfOak = {
  page: "onboarding-use-of-oak";
};

type PupilLessonCanonical = {
  page: "pupil-lesson-canonical";
  lessonSlug: string;
};

type PupilLessonCanonicalShared = {
  page: "pupil-lesson-canonical-shared";
  lessonSlug: string;
  shareVariant: string;
};

type MyLibraryProps = {
  page: "my-library";
};

type ProgrammePageProps = {
  page: "teacher-programme";
  subjectPhaseSlug: string;
  tab: string;
  query?: {
    keystages?: string;
    child_subjects?: string;
    tiers?: string;
    years?: string;
    pathways?: string;
    threads?: string;
    subject_categories?: string;
  };
};

type ClassroomSignInLinkProps = { page: "classroom-sign-in" };

type ClassroomBrowseLinkProps = { page: "classroom-browse" };

type ClassroomSubjectsLinkProps = { page: "classroom-subjects" };

type ClassroomOptionsLinkProps = { page: "classroom-options" };

type ClassroomUnitsLinkProps = { page: "classroom-units" };

type ClassroomLessonsLinkProps = { page: "classroom-lessons" };

type ClassroomAuthSuccessLinkProps = { page: "classroom-auth-success" };

type ClassroomPupilSignInLinkProps = { page: "classroom-pupil-sign-in" };

type EyfsPageLinkProps = {
  page: "eyfs-page";
  subjectSlug: string;
};

export type TeachWithOakQueryProps = {
  returnTo: string;
  lessonName: string;
  unitName: string;
};

type TeachWithOakDownloadLinkProps = {
  page: "teach-with-oak-download";
  query?: TeachWithOakQueryProps;
};

type TeachWithOakDownloadSuccessLinkProps = {
  page: "teach-with-oak-download-success";
  query?: TeachWithOakQueryProps;
};

type TeachWithOakLinkProps = {
  page: "teach-with-oak";
  query?: TeachWithOakQueryProps;
};

type OnlyPageRequired<T> = T extends { page: string }
  ? { page: T["page"] } extends T
    ? T
    : never
  : never;

export type OakLinkPropsRequiringPageOnly = Extract<
  OakLinkProps,
  OnlyPageRequired<OakLinkProps>
>;

export type OakLinkProps =
  | LabsLinkProps
  | LabsTeachingMaterialsLinkProps
  | McpLinkProps
  | TeachersHomePageProps
  | LandingPageLinkProps
  | LessonDownloadsLinkProps
  | LessonDownloadsSuccessLinkProps
  | LessonDownloadsCanonicalLinkProps
  | LessonMediaLinkProps
  | LessonMediaCanonicalLinkProps
  | LessonShareLinkProps
  | LessonOverviewLinkProps
  | PupilLessonLinkProps
  | PupilLessonResultsLinkProps
  | PupilLessonResultsCanonicalShareLinkProps
  | PupilLessonResultsCanonicalPrintableLinkProps
  | PupilLessonListingLinkProps
  | PupilUnitListingLinkProps
  | PupilSubjectListingLinkProps
  | PupilProgrammeListingLinkProps
  | PupilYearListingLinkProps
  | LessonOverviewCanonicalLinkProps
  | UnitOverviewLinkProps
  | BlogListingLinkProps
  | BlogSingleLinkProps
  | CampaignSingleLinkProps
  | WebinarListingLinkProps
  | WebinarSingleLinkProps
  | HelpLinkProps
  | PupilHelpLinkProps
  | LegalLinkProps
  | SearchLinkProps
  | AboutUsWhoWeAreLinkProps
  | AboutUsMeetTheTeamLinkProps
  | AboutUsMeetTheTeamBioLinkProps
  | AboutUsGetInvolvedLinkProps
  | AboutUsJoinResearchPanelLinkProps
  | AboutUsOaksCurriculaLinkProps
  | AboutUsOaksImpactLinkProps
  | AboutUsCaseStudyLinkProps
  | AboutUsCaseStudyLibraryLinkProps
  | CareersLinkProps
  | ContactUsLinkProps
  | HomeLinkProps
  | LessonPlanningLinkProps
  | CurriculumChangeExplainedLinkProps
  | OurTeachersLinkProps
  | OakCurriculumLinkProps
  | ClassroomLinkProps
  | TeacherHubLinkProps
  | OnboardingLinkProps
  | OnboardingSchoolSelectionLinkProps
  | OnboardingRoleSelectionLinkProps
  | OnboardingUseOfOak
  | PupilLessonCanonical
  | PupilLessonCanonicalShared
  | MyLibraryProps
  | ProgrammePageProps
  | ClassroomSignInLinkProps
  | ClassroomBrowseLinkProps
  | ClassroomSubjectsLinkProps
  | ClassroomOptionsLinkProps
  | ClassroomUnitsLinkProps
  | ClassroomLessonsLinkProps
  | ClassroomAuthSuccessLinkProps
  | ClassroomPupilSignInLinkProps
  | EyfsPageLinkProps
  | GuideToOakLinkProps
  | FontHelpLinkProps
  | TeachWithOakLinkProps
  | TeachWithOakDownloadLinkProps
  | TeachWithOakDownloadSuccessLinkProps;

export type ExternalPageName =
  | "[external] Careers"
  | "[external] Help"
  | "[external] Classroom"
  | "[external] Labs"
  | "[external] Our teachers"
  | "[external] Teacher hub"
  | "[external] Our curriculum";

type OakPages = {
  [K in OakLinkProps as K["page"]]: OakPageConfig<K>;
};

type OakPageConfig<
  ResolveHrefProps extends {
    page: string;
    query?: UrlQueryObject;
  },
> =
  | {
      analyticsPageName: PageNameValueType;
      pageType: ResolveHrefProps["page"];
      resolveHref: (props: ResolveHrefProps) => string;
      matchHref: MatchFunction<Omit<ResolveHrefProps, "page">>;
      configType: "internal" | "internal-custom-resolve";
    }
  | {
      analyticsPageName: ExternalPageName;
      pageType: ResolveHrefProps["page"];
      resolveHref: (props: ResolveHrefProps) => string;
      matchHref: MatchFunction<Omit<ResolveHrefProps, "page">>;
      configType: "external";
    };
export function createOakPageConfig<ResolveHrefProps extends OakLinkProps>(
  props:
    | {
        configType: "internal";
        pathPattern: string;
        analyticsPageName: PageNameValueType;
        pageType: ResolveHrefProps["page"];
      }
    | {
        configType: "internal-custom-resolve";
        resolveHref: (props: ResolveHrefProps) => string;
        matchHref: MatchFunction<Omit<ResolveHrefProps, "page">>;
        analyticsPageName: PageNameValueType;
        pageType: ResolveHrefProps["page"];
      }
    | {
        configType: "external";
        url: string;
        analyticsPageName: ExternalPageName;
        pageType: ResolveHrefProps["page"];
      },
): OakPageConfig<ResolveHrefProps> {
  switch (props.configType) {
    case "external":
      return {
        ...props,
        matchHref: () => false,
        resolveHref: (resolveHrefProps: ResolveHrefProps) => {
          if ("query" in resolveHrefProps && resolveHrefProps.query) {
            const queryString = createQueryStringFromObject(
              resolveHrefProps.query,
            );
            if (queryString) {
              return `${props.url}?${queryString}`;
            }
          }

          return props.url;
        },
      };
    case "internal":
      return {
        ...props,
        matchHref: match<Omit<ResolveHrefProps, "page">>(props.pathPattern, {
          decode: decodeURIComponent,
        }),
        resolveHref: (resolveHrefProps: ResolveHrefProps) => {
          const path = compile<Omit<ResolveHrefProps, "page">>(
            props.pathPattern,
            { encode: encodeURIComponent },
          )(resolveHrefProps);
          /**
           * @todo consolidate these -> query
           */
          if ("search" in resolveHrefProps) {
            return `${path}?${createQueryStringFromObject(
              resolveHrefProps.search,
            )}`;
          }
          if ("query" in resolveHrefProps) {
            return `${path}?${createQueryStringFromObject(
              resolveHrefProps.query,
            )}`;
          }
          return path;
        },
      };
    case "internal-custom-resolve":
      return {
        ...props,
      };
  }
}

const postMatchHref =
  <PostListingLinkProps extends BlogListingLinkProps | WebinarListingLinkProps>(
    postType: PostListingLinkProps["page"],
  ) =>
  (href: string) => {
    const path = postType === "blog-index" ? "/blog" : "/webinars";
    if (match(`${path}`)(href)) {
      return match<PostListingLinkProps>(path)(href);
    }
    if (match(`${path}/categories/:category`)(href)) {
      return match<PostListingLinkProps>(`${path}/categories/:category`)(href);
    }
    return false;
  };
const postResolveHref =
  <PostListingLinkProps extends BlogListingLinkProps | WebinarListingLinkProps>(
    postType: PostListingLinkProps["page"],
  ) =>
  (props: PostListingLinkProps) => {
    let path = postType === "blog-index" ? "/blog" : "/webinars";
    if (props.categorySlug) {
      path = `${path}/categories/${props.categorySlug}`;
    }
    if (!props.search) {
      return path;
    }
    const queryString = createQueryStringFromObject(props.search);

    if (!queryString) {
      return path;
    }

    return `${path}?${queryString}`;
  };

export const OAK_PAGES: {
  [K in keyof OakPages]: OakPages[K] & { pageType: K };
} = {
  "about-who-we-are": createOakPageConfig({
    pathPattern: "/about-us/who-we-are",
    analyticsPageName: "About Us: Who We Are",
    configType: "internal",
    pageType: "about-who-we-are",
  }),
  "about-meet-the-team": createOakPageConfig({
    pathPattern: "/about-us/meet-the-team",
    analyticsPageName: "About Us: Meet the team",
    configType: "internal",
    pageType: "about-meet-the-team",
  }),
  "about-meet-the-team-bio": createOakPageConfig({
    pathPattern: "/about-us/meet-the-team/:slug",
    analyticsPageName: "About Us: Meet the team Bio",
    configType: "internal",
    pageType: "about-meet-the-team-bio",
  }),
  "about-get-involved": createOakPageConfig({
    pathPattern: "/about-us/get-involved",
    analyticsPageName: "About Us: Get Involved",
    configType: "internal",
    pageType: "about-get-involved",
  }),
  "about-join-research-panel": createOakPageConfig({
    pathPattern: "/about-us/get-involved/join-research-panel",
    analyticsPageName: "About Us: Join Research Panel",
    configType: "internal",
    pageType: "about-join-research-panel",
  }),
  "about-oaks-curricula": createOakPageConfig({
    pathPattern: "/about-us/oaks-curricula",
    analyticsPageName: "About Us: Oak's curricula",
    configType: "internal",
    pageType: "about-oaks-curricula",
  }),
  "about-oaks-impact": createOakPageConfig({
    pathPattern: "/about-us/oaks-impact",
    analyticsPageName: "About Us: Oak's Impact",
    configType: "internal",
    pageType: "about-oaks-impact",
  }),
  "about-case-study": createOakPageConfig({
    pathPattern: "/about-us/case-studies/:slug",
    analyticsPageName: "About Us: Case Study",
    configType: "internal",
    pageType: "about-case-study",
  }),
  "about-case-study-library": createOakPageConfig({
    pathPattern: "/about-us/case-studies",
    analyticsPageName: "About Us: Case Study Library",
    configType: "internal",
    pageType: "about-case-study-library",
  }),
  careers: createOakPageConfig({
    url: "https://app.beapplied.com/org/1574/oak-national-academy",
    analyticsPageName: "[external] Careers",
    configType: "external",
    pageType: "careers",
  }),
  contact: createOakPageConfig({
    pathPattern: "/contact-us",
    analyticsPageName: "Contact Us",
    configType: "internal",
    pageType: "contact",
  }),
  help: createOakPageConfig({
    url: "https://support.thenational.academy",
    analyticsPageName: "[external] Help",
    configType: "external",
    pageType: "help",
  }),
  "pupil-help": createOakPageConfig({
    url: "https://support.thenational.academy/our-pupil-area",
    analyticsPageName: "[external] Help",
    configType: "external",
    pageType: "pupil-help",
  }),
  "guide-to-oak": createOakPageConfig({
    url: "https://support.thenational.academy/guide-how-to-use-oak",
    analyticsPageName: "[external] Help",
    configType: "external",
    pageType: "guide-to-oak",
  }),
  "help-font": createOakPageConfig({
    url: "https://support.thenational.academy/how-to-install-the-google-fonts-lexend-and-kalan",
    analyticsPageName: "[external] Help",
    configType: "external",
    pageType: "help-font",
  }),
  home: createOakPageConfig({
    pathPattern: "/",
    analyticsPageName: "Homepage",
    configType: "internal",
    pageType: "home",
  }),
  "lesson-planning": createOakPageConfig({
    pathPattern: "/lesson-planning",
    analyticsPageName: "Plan a Lesson",
    configType: "internal",
    pageType: "lesson-planning",
  }),
  "curriculum-change-explained": createOakPageConfig({
    pathPattern: nationalCurriculumInsightsGuidanceHref(),
    analyticsPageName: "Landing Page",
    configType: "internal",
    pageType: "curriculum-change-explained",
  }),
  legal: createOakPageConfig({
    pathPattern: "/legal/:legalSlug",
    analyticsPageName: "Legal",
    configType: "internal",
    pageType: "legal",
  }),
  classroom: createOakPageConfig({
    url: "https://classroom.thenational.academy",
    analyticsPageName: "[external] Classroom",
    configType: "external",
    pageType: "classroom",
  }),
  labs: createOakPageConfig({
    url: "https://labs.thenational.academy",
    analyticsPageName: "[external] Labs",
    configType: "external",
    pageType: "labs",
  }),
  "labs-teaching-materials": createOakPageConfig({
    url: "https://labs.thenational.academy/aila/teaching-materials",
    analyticsPageName: "[external] Labs",
    configType: "external",
    pageType: "labs-teaching-materials",
  }),
  mcp: createOakPageConfig({
    pathPattern: "/ai-plugin",
    analyticsPageName: "AI Plugin",
    configType: "internal",
    pageType: "mcp",
  }),
  "our-teachers": createOakPageConfig({
    url: "https://classroom.thenational.academy/teachers",
    analyticsPageName: "[external] Our teachers",
    configType: "external",
    pageType: "our-teachers",
  }),
  "teacher-hub": createOakPageConfig({
    url: "https://teachers.thenational.academy",
    analyticsPageName: "[external] Teacher hub",
    configType: "external",
    pageType: "teacher-hub",
  }),
  "oak-curriculum": createOakPageConfig({
    url: "https://teachers.thenational.academy/oaks-curricula",
    analyticsPageName: "[external] Our curriculum",
    configType: "external",
    pageType: "oak-curriculum",
  }),
  "blog-index": createOakPageConfig({
    analyticsPageName: "Blog Listing",
    configType: "internal-custom-resolve",
    pageType: "blog-index",
    matchHref: postMatchHref("blog-index"),
    resolveHref: postResolveHref("blog-index"),
  }),
  "webinar-index": createOakPageConfig({
    analyticsPageName: "Webinar Listing",
    configType: "internal-custom-resolve",
    pageType: "webinar-index",
    matchHref: postMatchHref("webinar-index"),
    resolveHref: postResolveHref("webinar-index"),
  }),
  "unit-overview": createOakPageConfig({
    pathPattern: "/teachers/programmes/:programmeSlug/units/:unitSlug/lessons",
    analyticsPageName: "Lesson Listing",
    configType: "internal",
    pageType: "unit-overview",
  }),
  "lesson-overview": createOakPageConfig({
    pathPattern:
      "/teachers/programmes/:programmeSlug/units/:unitSlug/lessons/:lessonSlug",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "lesson-overview",
  }),
  "lesson-downloads": createOakPageConfig({
    pathPattern:
      "/teachers/programmes/:programmeSlug/units/:unitSlug/lessons/:lessonSlug/downloads",
    analyticsPageName: "Lesson Download",
    configType: "internal",
    pageType: "lesson-downloads",
  }),
  "lesson-downloads-success": createOakPageConfig({
    pathPattern:
      "/teachers/programmes/:programmeSlug/units/:unitSlug/lessons/:lessonSlug/downloads/success",
    analyticsPageName: "Lesson Download",
    configType: "internal",
    pageType: "lesson-downloads-success",
  }),
  "lesson-media": createOakPageConfig({
    pathPattern:
      "/teachers/programmes/:programmeSlug/units/:unitSlug/lessons/:lessonSlug/media",
    analyticsPageName: "Lesson Media",
    configType: "internal",
    pageType: "lesson-media",
  }),
  "pupil-lesson": createOakPageConfig({
    pathPattern:
      "/pupils/programmes/:programmeSlug/units/:unitSlug/lessons/:lessonSlug/overview",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "pupil-lesson",
  }),
  "pupil-lesson-results": createOakPageConfig({
    pathPattern:
      "/pupils/programmes/:programmeSlug/units/:unitSlug/lessons/:lessonSlug/results/:attemptId",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "pupil-lesson-results",
  }),
  "pupil-lesson-results-canonical-share": createOakPageConfig({
    pathPattern: "/pupils/lessons/:lessonSlug/results/:attemptId/share",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "pupil-lesson-results-canonical-share",
  }),
  "pupil-lesson-results-canonical-printable": createOakPageConfig({
    pathPattern: "/pupils/lessons/:lessonSlug/results/:attemptId/printable",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "pupil-lesson-results-canonical-printable",
  }),
  "pupil-lesson-canonical": createOakPageConfig({
    pathPattern: "/pupils/lessons/:lessonSlug",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "pupil-lesson-canonical",
  }),
  "pupil-lesson-canonical-shared": createOakPageConfig({
    pathPattern: "/pupils/lessons/:lessonSlug/shared/:shareVariant/overview",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "pupil-lesson-canonical-shared",
  }),
  "pupil-lesson-index": createOakPageConfig({
    pathPattern: "/pupils/programmes/:programmeSlug/units/:unitSlug/lessons",
    analyticsPageName: "Lesson Listing",
    configType: "internal",
    pageType: "pupil-lesson-index",
  }),
  "pupil-unit-index": createOakPageConfig({
    pathPattern: "/pupils/programmes/:programmeSlug/units",
    analyticsPageName: "Unit Listing",
    configType: "internal",
    pageType: "pupil-unit-index",
  }),
  "pupil-programme-index": createOakPageConfig({
    pathPattern: "/pupils/programmes/:programmeSlug/:optionSlug",
    analyticsPageName: "Programme Listing",
    configType: "internal",
    pageType: "pupil-programme-index",
  }),
  "pupil-subject-index": createOakPageConfig({
    pathPattern: "/pupils/years/:yearSlug/subjects",
    analyticsPageName: "Subject Listing",
    configType: "internal",
    pageType: "pupil-subject-index",
  }),
  "pupil-year-index": createOakPageConfig({
    pathPattern: "/pupils/years",
    analyticsPageName: "Subject Listing",
    configType: "internal",
    pageType: "pupil-year-index",
  }),
  "lesson-overview-canonical": createOakPageConfig({
    pathPattern: "/teachers/lessons/:lessonSlug",
    analyticsPageName: "Lesson",
    configType: "internal",
    pageType: "lesson-overview-canonical",
  }),
  "lesson-downloads-canonical": createOakPageConfig({
    // @TODO revert `:downloads` to `download` when auth is no longer behind a feature flag
    pathPattern: "/teachers/lessons/:lessonSlug/:downloads",
    analyticsPageName: "Lesson Download",
    configType: "internal",
    pageType: "lesson-downloads-canonical",
  }),
  "lesson-media-canonical": createOakPageConfig({
    pathPattern: "/teachers/lessons/:lessonSlug/media",
    analyticsPageName: "Lesson Media",
    configType: "internal",
    pageType: "lesson-media-canonical",
  }),
  "lesson-share": createOakPageConfig({
    pathPattern:
      "/teachers/programmes/:programmeSlug/units/:unitSlug/lessons/:lessonSlug/share",
    analyticsPageName: "Lesson Share",
    configType: "internal",
    pageType: "lesson-share",
  }),
  search: createOakPageConfig({
    pathPattern: "/teachers/search",
    analyticsPageName: "Search",
    configType: "internal",
    pageType: "search",
  }),
  "blog-single": createOakPageConfig({
    pathPattern: "/blog/:blogSlug",
    analyticsPageName: "Blog",
    configType: "internal",
    pageType: "blog-single",
  }),
  "campaign-single": createOakPageConfig({
    analyticsPageName: "Campaign",
    configType: "internal",
    pageType: "campaign-single",
    pathPattern: "/campaigns/:campaignSlug",
  }),
  "webinar-single": createOakPageConfig({
    pathPattern: "/webinars/:webinarSlug",
    analyticsPageName: "Webinar",
    configType: "internal",
    pageType: "webinar-single",
  }),
  "landing-page": createOakPageConfig({
    pathPattern: "/lp/:lpSlug",
    analyticsPageName: "Landing Page",
    configType: "internal",
    pageType: "landing-page",
  }),
  onboarding: createOakPageConfig({
    pathPattern: "/onboarding",
    analyticsPageName: "Onboarding: Work In School",
    configType: "internal",
    pageType: "onboarding",
  }),
  "onboarding-school-selection": createOakPageConfig({
    pathPattern: "/onboarding/school-selection",
    analyticsPageName: "Onboarding: School Selection",
    configType: "internal",
    pageType: "onboarding-school-selection",
  }),
  "onboarding-role-selection": createOakPageConfig({
    pathPattern: "/onboarding/role-selection",
    analyticsPageName: "Onboarding: Role Selection",
    configType: "internal",
    pageType: "onboarding-role-selection",
  }),
  "onboarding-use-of-oak": createOakPageConfig({
    pathPattern: "/onboarding/how-can-oak-support-you",
    analyticsPageName: "Onboarding: Use Of Oak",
    configType: "internal",
    pageType: "onboarding-use-of-oak",
  }),
  "teachers-home-page": createOakPageConfig({
    pathPattern: "/",
    analyticsPageName: "Homepage",
    configType: "internal",
    pageType: "teachers-home-page",
  }),
  "my-library": createOakPageConfig({
    pathPattern: "/teachers/my-library",
    analyticsPageName: "My library",
    configType: "internal",
    pageType: "my-library",
  }),
  "teacher-programme": createOakPageConfig({
    pathPattern: "/teachers/programmes/:subjectPhaseSlug/:tab",
    analyticsPageName: "Curriculum Unit Sequence",
    configType: "internal",
    pageType: "teacher-programme",
  }),
  "classroom-sign-in": createOakPageConfig({
    pathPattern: "/classroom/sign-in",
    analyticsPageName: "Classroom: Sign In",
    configType: "internal",
    pageType: "classroom-sign-in",
  }),
  "classroom-browse": createOakPageConfig({
    pathPattern: "/classroom/browse",
    analyticsPageName: "Classroom: Browse Years",
    configType: "internal",
    pageType: "classroom-browse",
  }),
  "classroom-subjects": createOakPageConfig({
    pathPattern: "/classroom/browse/years/:yearSlug/subjects",
    analyticsPageName: "Classroom: Subjects",
    configType: "internal",
    pageType: "classroom-subjects",
  }),
  "classroom-options": createOakPageConfig({
    pathPattern: "/classroom/browse/programmes/:programmeSlug/options",
    analyticsPageName: "Classroom: Programme Options",
    configType: "internal",
    pageType: "classroom-options",
  }),
  "classroom-units": createOakPageConfig({
    pathPattern: "/classroom/browse/programmes/:programmeSlug/units",
    analyticsPageName: "Classroom: Units",
    configType: "internal",
    pageType: "classroom-units",
  }),
  "classroom-lessons": createOakPageConfig({
    pathPattern:
      "/classroom/browse/programmes/:programmeSlug/units/:unitSlug/lessons",
    analyticsPageName: "Classroom: Lessons",
    configType: "internal",
    pageType: "classroom-lessons",
  }),
  "classroom-auth-success": createOakPageConfig({
    pathPattern: "/classroom/auth/success",
    analyticsPageName: "Classroom: Auth Success",
    configType: "internal",
    pageType: "classroom-auth-success",
  }),
  "classroom-pupil-sign-in": createOakPageConfig({
    pathPattern: "/classroom/pupil/sign-in",
    analyticsPageName: "Classroom: Pupil Sign In",
    configType: "internal",
    pageType: "classroom-pupil-sign-in",
  }),
  "eyfs-page": createOakPageConfig({
    pathPattern: "/teachers/eyfs/:subjectSlug",
    analyticsPageName: "Unit Listing",
    configType: "internal",
    pageType: "eyfs-page",
  }),
  "teach-with-oak": createOakPageConfig({
    pathPattern: "/teachers/teach-with-oak",
    analyticsPageName: "Teach With Oak",
    configType: "internal",
    pageType: "teach-with-oak",
  }),
  "teach-with-oak-download": createOakPageConfig({
    pathPattern: "/teachers/teach-with-oak/downloads",
    analyticsPageName: "Teach With Oak",
    configType: "internal",
    pageType: "teach-with-oak-download",
  }),
  "teach-with-oak-download-success": createOakPageConfig({
    pathPattern: "/teachers/teach-with-oak/downloads/success",
    analyticsPageName: "Teach With Oak",
    configType: "internal",
    pageType: "teach-with-oak-download-success",
  }),
};

export type ResolveOakHrefProps = Exclude<
  Parameters<OakPages[keyof OakPages]["resolveHref"]>[number],
  void
>;

/**
 * Pass readable props which are unlikely to need to change, and return an href.
 * @example
 * resolveOakHref({ page: "teacher-hub "})
 * resolveOakHref({ page: "pupils-lesson", lessonSlug: "spreadsheet-warm-up-75j64r" })
 * resolveOakHref({ page: "blog", blogSlug: "how-oak-helps-everyone" })
 */
export function resolveOakHref(props: ResolveOakHrefProps): string {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const path = OAK_PAGES[props.page].resolveHref(props);
    return path;
  } catch (error) {
    const err = new OakError({
      code: "urls/failed-to-resolve",
      originalError: error,
      meta: props,
    });
    reportError(err);

    return "/";
  }
}

/**
 * Extract oak href params from a passed in path string
 * or return false if the path does not match the expected page type
 */
type MatchPropsForPage<TPage extends OakPageType> = Omit<
  Extract<OakLinkProps, { page: TPage }>,
  "page"
>;

export function matchOakHref<TPage extends OakPageType>(
  path: string,
  page: TPage,
): false | MatchResult<MatchPropsForPage<TPage>>;
export function matchOakHref(
  path: string,
  page: OakPageType,
): false | MatchResult<Omit<OakLinkProps, "page">> {
  return OAK_PAGES[page].matchHref(path);
}
